// Copyright 2017 Sorama B.V.

import fastDeepEqual from "fast-deep-equal";
import _ from "lodash";

export class ObjectExtensions {
    public static forEachProperty(obj: object, action: (key: string, value: unknown) => void): void {
        for (const key in obj) {
            if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

            action(key, obj[key]);
        }
    }

    public static forEachPropertyInSet<T>(obj: { [key: string]: T }, action: (key: string, value: T) => void): void {
        for (const key in obj) {
            if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

            action(key, obj[key]);
        }
    }

    /**
     * Expands the provided path in the object property tree and checks at every node if the property exists to prevent undefined references.
     * If a an null or undefined in encountered anywhere in the path, undefined is returned otherwise the value of the object to which the path refers.
     * @param obj Object to expand the path in.
     * @param path Set of properties to expand.
     */
    public static safeGetOptionalPath(obj: unknown, ...path: string[]): unknown {
        let propVal = obj;
        for (let i = 0; i < path.length; i++) {
            if (propVal === null || propVal === undefined) {
                return undefined;
            }

            const propName = path[i];
            propVal = propVal[propName];
        }

        return propVal;
    }

    /**
     * Deep clones plain data objects.
     * Does NOT work for class instances with methods or getters/setters, as these will lose their prototype and become plain objects. For such cases, consider implementing a custom clone method on the class.
     * @param obj Object to be copied.
     * @returns A deep copy of the provided object.
     */
    public static copy<T>(obj: T): T {
        if (!obj) return obj;
        try {
            return <T>structuredClone(obj);
        } catch {
            return _.cloneDeep(obj);
        }
    }

    public static deepEqual<T>(obj1: T, obj2: T): boolean {
        return fastDeepEqual(obj1, obj2);
    }

    public static deepMerge(target, source) {
        for (const key in source) {
            if (source[key] instanceof Object && key in target) {
                Object.assign(source[key], this.deepMerge(target[key], source[key]));
            }
        }
        Object.assign(target || {}, source);
        return target;
    }
}
